// "Fissa visita" (components/staff/FissaVisita.jsx): calendario della settimana dalle 8 alle 20, mezz'ora per riga,
// con le attività di chi accompagna; le fasce in cui il cliente non è disponibile; sovrapposizioni; slot consigliati.
import { aggiungiGiorni, daIso, isoGiorno, MESI } from './agenda';

export const INIZIO = 8 * 60;
export const FINE = 20 * 60;
export const PASSO = 30;
export const RIGHE = Array.from({ length: (FINE - INIZIO) / PASSO }, (_, i) => INIZIO + i * PASSO);  // 8:00 … 19:30
export const DURATE = [30, 45, 60, 90];
export const DURATA_BASE = 45;
// "Tre case, stesso pomeriggio": il tempo di salutare e spostarsi fra una casa e l'altra dello stesso paese.
// Stessi valori del backend (backend/agenzia_case.py: PAUSA_MINUTI, DURATA_BASE, orari_in_fila).
export const PAUSA = 15;
// Quanto occupa un'attività senza durata sua
export const DURATE_TIPO = { visita: 60, telefonata: 15, rogito: 60, scadenza: 30, altro: 30, nota: 30, documento: 30 };
const GIORNI_LUNGHI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const GIORNI_BREVI = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
// Dove si cercano gli slot consigliati, per fascia (minuti di inizio)
const CANDIDATI = { mattina: [540, 570, 600, 630, 660, 690, 720, 750], pomeriggio: [870, 900, 930, 960, 990, 1020, 1050], sera: [1080, 1110, 1140] };

export const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
export const minutiDa = (testo) => {
  const [h, m] = String(testo || '').split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
export const fasciaDi = (min) => (min < 13 * 60 ? 'mattina' : min < 18 * 60 ? 'pomeriggio' : 'sera');
export const lunediDi = (iso) => aggiungiGiorni(iso, -((daIso(iso).getDay() + 6) % 7));
export const giorniSettimana = (lunedi) => Array.from({ length: 7 }, (_, i) => aggiungiGiorni(lunedi, i));

// Stessa regola del backend (disponibilita.fasce_libere): le eccezioni vincono sulla settimana
export const fasceLibere = (disp, iso) => {
  if (!disp) return [];
  if (disp.eccezioni && iso in disp.eccezioni) return disp.eccezioni[iso] || [];
  const griglia = disp.ogni_settimana !== false ? disp.settimana : (disp.settimane || {})[lunediDi(iso)];
  return (griglia || {})[String((daIso(iso).getDay() + 6) % 7)] || [];
};
export const dispVuota = (disp) => !disp || (!Object.keys(disp.settimana || {}).length && !Object.values(disp.settimane || {}).some((g) => Object.keys(g || {}).length));

// Il cliente è disponibile a quell'ora? Senza disponibilità indicata non si segna niente in rosso
export const disponibile = (disp, iso, min) => dispVuota(disp) || fasceLibere(disp, iso).includes(fasciaDi(min));

// Il titolo di una voce nel calendario, come lo scrive CosaVoce: azione, nome dell'agenzia (se non è già dentro la
// frase) e casa. Prima era il solo `cosa.azione` e nel calendario restava "Richiamare" senza dire chi né per quale
// casa ("10:30 Richiamare · Олена Ш.").
export const titoloCosa = (cosa, titolo = '') => {
  if (!cosa) return titolo;
  const azione = cosa.azione || titolo || '';
  const nome = cosa.agenzia?.nome;
  const conAgenzia = nome && !cosa.agenzia.nel_testo ? `${azione} ${nome}`.trim() : azione;
  return [conAgenzia, cosa.immobile?.nome].filter(Boolean).join(' · ') || titolo;
};

// "via Rosa del Sasso 8", o un ripiego leggibile per un immobile ancora senza indirizzo né titolo (nel menu
// "Quale immobile?" era una riga vuota, che si poteva perfino scegliere)
export const etichettaImmobile = (i) => (i && (i.indirizzo || i.titolo))
  || (i?.citta ? `Casa senza indirizzo · ${i.citta}` : 'Casa senza indirizzo');

// Le attività con un orario come blocchi {id, data, inizio, fine, tipo, titolo, cliente}
export const blocchiDa = (voci) => (voci || [])
  .filter((v) => v.ora && !v.tutto_il_giorno && v.stato !== 'annullata')
  .map((v) => {
    const inizio = minutiDa(v.ora);
    const durata = Number(v.dettaglio?.durata) || DURATE_TIPO[v.tipo] || 30;
    return { id: v.id, data: v.data, inizio, fine: inizio + durata, tipo: v.tipo, titolo: titoloCosa(v.cosa, v.titolo), ora: v.ora,
      cliente: v.cliente_breve || null, visitaId: v.rif?.visita_id || null };
  });

// Le visite già fissate del cliente (anche con altri accompagnatori), in evidenza nel calendario
export const blocchiVisiteCliente = (visite, nome) => (visite || [])
  .filter((v) => !v.annullata && v.data_visita)
  .map((v) => {
    const [data, ora] = String(v.data_visita).slice(0, 16).split('T');
    const inizio = minutiDa(ora);
    return { id: `cliente-${v.id}`, visitaId: v.id, data, inizio, fine: inizio + (Number(v.durata_minuti) || 60), tipo: 'visita', ora: ora.slice(0, 5),
      titolo: `Visita ${v.immobile_indirizzo || v.immobile_titolo || ''}`.trim(), cliente: nome || null, delCliente: true };
  });

export const sovrapposte = (blocchi, iso, inizio, durata) => (blocchi || []).filter((b) => b.data === iso && b.inizio < inizio + durata && inizio < b.fine);

// ---------------------------------------------------------------- tre case, stesso pomeriggio

// Gli orari di N visite di fila dallo stesso inizio (minuti dalla mezzanotte): 15:00, 16:00, 17:00 con visite da
// 45' e 15' di pausa. Stesso conto di agenzia_case.orari_in_fila, con gli stessi valori di base.
export const orariInFila = (inizio, quante, durata = DURATA_BASE, pausa = PAUSA) =>
  Array.from({ length: Math.max(0, Number(quante) || 0) }, (_, n) => inizio + n * (durata + pausa));

// Quanto dura tutta la fila, pause comprese: serve agli slot consigliati e all'evidenza nel calendario
export const durataFila = (quante, durata = DURATA_BASE, pausa = PAUSA) =>
  (quante > 0 ? quante * durata + (quante - 1) * pausa : durata);

const FILA_FASCIA = { mattina: 'stessa mattina', pomeriggio: 'stesso pomeriggio', sera: 'stessa sera' };
// "3 case, stesso pomeriggio"
export const titoloFila = (quante, inizio) => `${quante} case, ${FILA_FASCIA[fasciaDi(inizio)]}`;

// Sicurezza in più: due visite della fila non devono accavallarsi (il server risponde 409, ma è meglio dirlo prima)
export const filaSiAccavalla = (orari, durata) => (orari || []).some((o, n) => n > 0 && o < orari[n - 1] + durata);

// "15:00 · via Manzoni 14 — 16:00 · via Cavour 3 — 17:00 · via Dante 9"
export const righeFila = (case_, orari) => (case_ || [])
  .map((c, n) => `${hhmm(orari[n])} · ${c.indirizzo || c.titolo || ''}`).join(' — ');

export const passato = (iso, min, adesso) => new Date(`${iso}T${hhmm(min)}:00`) <= adesso;

// "Sabato 19, 10:30 – 11:15"
export const testoSlot = (iso, inizio, durata) => {
  const d = daIso(iso);
  const giorno = GIORNI_LUNGHI[d.getDay()];
  return `${giorno[0].toUpperCase()}${giorno.slice(1)} ${d.getDate()}, ${hhmm(inizio)} – ${hhmm(inizio + durata)}`;
};
export const testoBreve = (iso, inizio) => `${GIORNI_BREVI[daIso(iso).getDay()]} ${daIso(iso).getDate()} ${hhmm(inizio)}`;
// Il giorno da solo, per la barra del calendario sul telefono (dove si guarda un giorno alla volta): "gio 18 settembre"
export const etichettaGiorno = (iso) => {
  const d = daIso(iso);
  return `${GIORNI_BREVI[d.getDay()]} ${d.getDate()} ${MESI[d.getMonth()]}`;
};
export const intervallo = (lunedi) => {
  const a = daIso(lunedi);
  const b = daIso(aggiungiGiorni(lunedi, 6));
  return a.getMonth() === b.getMonth() ? `${a.getDate()} – ${b.getDate()} ${MESI[b.getMonth()]}` : `${a.getDate()} ${MESI[a.getMonth()].slice(0, 3)} – ${b.getDate()} ${MESI[b.getMonth()].slice(0, 3)}`;
};

// I primi slot liberi per chi accompagna e disponibili per il cliente nei prossimi giorni: al massimo uno per fascia
// di ogni giorno, almeno un'ora da adesso, dentro le 8-20. Senza disponibilità del cliente: tutte le fasce.
// Mai di domenica (le agenzie sono chiuse): prima si scartava solo quando il cliente non aveva segnato niente, e a
// un cliente disponibile la domenica veniva consigliato un giorno in cui la visita non si può fare.
export const slotConsigliati = ({ adesso, disp, blocchi, durata = DURATA_BASE, giorni = 10, quanti = 5 }) => {
  const oggi = isoGiorno(adesso);
  const presto = new Date(adesso.getTime() + 60 * 60000);
  const vuota = dispVuota(disp);
  const trovati = [];
  for (let g = 0; g <= giorni && trovati.length < quanti; g += 1) {
    const iso = aggiungiGiorni(oggi, g);
    if (daIso(iso).getDay() === 0) continue;
    const fasce = vuota ? ['mattina', 'pomeriggio', 'sera'] : fasceLibere(disp, iso);
    for (const fascia of ['mattina', 'pomeriggio', 'sera']) {
      if (!fasce.includes(fascia) || trovati.length >= quanti) continue;
      const inizio = CANDIDATI[fascia].find((m) => m + durata <= FINE && new Date(`${iso}T${hhmm(m)}:00`) >= presto
        && !sovrapposte(blocchi, iso, m, durata).length);
      if (inizio !== undefined) trovati.push({ data: iso, inizio, fascia });
    }
  }
  return trovati;
};
